import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, Paginated } from '../types';

import type { LeaderboardRow } from './leaderboard';

// ── Season shapes ────────────────────────────────────────────────────

export interface SeasonPodiumEntry {
  rank: number;
  name: string;
  avatar: string | null;
  /** Summed live from the standings, like the legacy season list. */
  games: number;
  points: number;
}

/** `GET /api/seasons` row (paginated). */
export interface SeasonSummary {
  num: number;
  gamesCount: number;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  endsInDays: number | null;
  podium: SeasonPodiumEntry[];
}

/**
 * `GET /api/seasons/{num}` - summary + the first 100-row page of the standings
 * (the leaderboard endpoint pages the rest).
 */
export interface SeasonDetail extends SeasonSummary {
  standings: LeaderboardRow[];
}

/** `GET /api/seasons` - paginated season list (newest first). */
export function useSeasons(page = 1): UseQueryResult<Paginated<SeasonSummary>> {
  return useQuery({
    queryKey: queryKeys.seasons.list(page),
    placeholderData: keepPreviousData,
    queryFn: () => api.get<Paginated<SeasonSummary>>('/seasons', { query: { page } }),
  });
}

/** `GET /api/seasons/{num}` - season detail + standings. */
export function useSeason(num: number | undefined): UseQueryResult<SeasonDetail> {
  return useQuery({
    queryKey: queryKeys.seasons.detail(num ?? 0),
    enabled: num !== undefined,
    queryFn: async () => {
      const res = await api.get<Envelope<SeasonDetail>>(
        `/seasons/${encodeURIComponent(String(num))}`,
      );
      return res.data;
    },
  });
}
